import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
// @ts-ignore
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import ContentWrapper from "../../components/content-wrapper";
import { SectionHeading } from "../../components/section-heading";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Image,
  ImageManagerProvider,
  getDataByActions,
  renderImageComponent,
} from "../shared/widgets/ImageManager";

import {
  MarketingManagerSchema,
  MarketingManagerFormValues,
  MarketingManagerEntity as MarketingManagerEntityClass,
} from "./domain/marketingManager.entity";

import {
  getMarketingImage,
  updateMarketingImage,
  uploadMarketingImage,
  deleteMarketingImage,
  MarketingMediaType,
} from "./infrastructure/marketingManager.api";

import { IHttpClient, useHttpClient } from "../../lib/http";
import { BACKOFFICE_MANAGER_URL } from "../witadmin/request";
import { marketingManagerMessages } from "./messages";
import {
  PageContainer,
  SectionCard,
  SectionCardBody,
  SectionTitle,
  SectionText,
  FormGroup,
  ErrorText,
  ButtonContainer,
  SectionDivider,
  UrlInputWrapper,
} from "./styles";

const sharedImageManagerConfig = {
  locales: [],
  maxFiles: 1,
  allowEdit: false,
  allowDeleteSelection: true,
  addBackgroundToPreview: true,
  formats: "png, jpeg, jpg",
  hideLimitMessage: true,
};

//Image field wrapper

interface MarketingImageFieldProps {
  fieldId: string;
  value: Image[];
  onChange: (images: Image[]) => void;
  isDisabled?: boolean;
}

function MarketingImageField({
  fieldId,
  value,
  onChange,
  isDisabled = false,
}: MarketingImageFieldProps) {
  return renderImageComponent({
    config: sharedImageManagerConfig,
    schema: { title: "" },
    idSchema: { $id: fieldId },
    formData: value,
    onChange,
    isDisabled,
  });
}

async function syncImage(
  httpClient: IHttpClient,
  original: Image[],
  next: Image[],
  mediaType: MarketingMediaType
) {
  const diff = getDataByActions(original, next);
  if (diff.create[0]) {
    await uploadMarketingImage(httpClient, mediaType, diff.create[0]);
  } else if (diff.remove.length > 0) {
    await deleteMarketingImage(httpClient, mediaType);
  }
}

//Main component

const HTTP_CLIENT_OPTIONS = { usePropertyHeader: false };

interface MarketingManagerFormProps {
  section?: string;
}

function MarketingManagerForm(_props: MarketingManagerFormProps) {
  const intl = useIntl();
  const history = useHistory();
  const httpClient = useHttpClient(BACKOFFICE_MANAGER_URL, HTTP_CLIENT_OPTIONS);

  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Keep a snapshot of the original images to compute create/delete diffs
  const originalImages = useRef<{ desktop: Image[]; mobile: Image[] }>({
    desktop: [],
    mobile: [],
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MarketingManagerFormValues>({
    resolver: zodResolver(MarketingManagerSchema),
    defaultValues: {
      desktopImages: [],
      mobileImages: [],
      startDate: undefined,
      endDate: undefined,
      redirectUrl: undefined,
    },
  });

  const resetRef = useRef(reset);
  resetRef.current = reset;

  // Load existing data on mount
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const entity = await getMarketingImage(httpClient);
        originalImages.current = {
          desktop: entity.desktopImages,
          mobile: entity.mobileImages,
        };
        resetRef.current({
          desktopImages: entity.desktopImages,
          mobileImages: entity.mobileImages,
          startDate: entity.startDate,
          endDate: entity.endDate,
          redirectUrl: entity.redirectUrl,
        });
      } catch (e) {
        console.error("Error loading marketing image", e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [httpClient]);

  const onSubmit = useCallback(
    async (data: MarketingManagerFormValues) => {
      setSaveError(undefined);
      setSaveSuccess(false);
      try {
        await Promise.all([
          syncImage(
            httpClient,
            originalImages.current.desktop,
            data.desktopImages as Image[],
            MarketingMediaType.DESKTOP
          ),
          syncImage(
            httpClient,
            originalImages.current.mobile,
            data.mobileImages as Image[],
            MarketingMediaType.MOBILE
          ),
        ]);
        await updateMarketingImage(
          httpClient,
          MarketingManagerEntityClass.create({
            id: "marketing-login-image",
            desktopImages: data.desktopImages as Image[],
            mobileImages: data.mobileImages as Image[],
            startDate: data.startDate ?? undefined,
            endDate: data.endDate ?? undefined,
            redirectUrl: data.redirectUrl || undefined,
          })
        );
        originalImages.current = {
          desktop: data.desktopImages as Image[],
          mobile: data.mobileImages as Image[],
        };
        setSaveSuccess(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        setSaveError(intl.formatMessage(marketingManagerMessages.saveError));
      }
    },
    [intl, httpClient]
  );

  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  if (isLoading) {
    return (
      <ContentWrapper>
        <div className="p-4 text-center">
          <span className="text-muted">
            <FormattedMessage id="app.loading" defaultMessage="Loading..." />
          </span>
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ImageManagerProvider>
      <ContentWrapper>
        <PageContainer>
          <SectionHeading>
            <FormattedMessage {...marketingManagerMessages.title} />
          </SectionHeading>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {saveSuccess && (
              <Alert
                variant="success"
                className="mx-4 mt-3"
                dismissible
                onClose={() => setSaveSuccess(false)}>
                <FormattedMessage {...marketingManagerMessages.saveSuccess} />
              </Alert>
            )}
            {saveError && (
              <Alert
                variant="danger"
                className="mx-4 mt-3"
                dismissible
                onClose={() => setSaveError(undefined)}>
                {saveError}
              </Alert>
            )}

            {/* Validez section */}
            <SectionCard>
              <SectionCardBody>
                <SectionTitle>
                  <FormattedMessage {...marketingManagerMessages.validityTitle} />
                </SectionTitle>
                <SectionText>
                  <FormattedMessage {...marketingManagerMessages.validityDescription} />
                </SectionText>

                <div className="row mt-4">
                  <div className="col-md-2">
                    <Controller
                      control={control}
                      name="startDate"
                      render={({ field }) => (
                        <FormGroup>
                          <label htmlFor="startDate">
                            <FormattedMessage {...marketingManagerMessages.startDate} />
                          </label>
                          <DatePicker
                            id="startDate"
                            selected={field.value ? new Date(field.value) : null}
                            onChange={(date: Date | null) =>
                              field.onChange(date ? date.toISOString() : null)
                            }
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            isClearable
                            placeholderText="yyyy-mm-dd"
                          />
                          {errors.startDate && (
                            <ErrorText>
                              <FormattedMessage
                                {...marketingManagerMessages.validationEndDateAfterStartDate}
                              />
                            </ErrorText>
                          )}
                        </FormGroup>
                      )}
                    />
                  </div>
                  <div className="col-md-2">
                    <Controller
                      control={control}
                      name="endDate"
                      render={({ field }) => (
                        <FormGroup>
                          <label htmlFor="endDate">
                            <FormattedMessage {...marketingManagerMessages.endDate} />
                          </label>
                          <DatePicker
                            id="endDate"
                            selected={field.value ? new Date(field.value) : null}
                            onChange={(date: Date | null) =>
                              field.onChange(date ? date.toISOString() : null)
                            }
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            isClearable
                            placeholderText="yyyy-mm-dd"
                          />
                          {errors.endDate && (
                            <ErrorText>
                              <FormattedMessage
                                {...marketingManagerMessages.validationEndDateAfterStartDate}
                              />
                            </ErrorText>
                          )}
                        </FormGroup>
                      )}
                    />
                  </div>
                </div>

                {/* Redirect URL */}
                <SectionDivider>
                  <Controller
                    control={control}
                    name="redirectUrl"
                    render={({ field }) => (
                      <FormGroup>
                        <label htmlFor="redirectUrl">
                          <FormattedMessage {...marketingManagerMessages.redirectUrl} />
                        </label>
                        <UrlInputWrapper>
                          {/* Link icon */}
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                          <input
                            id="redirectUrl"
                            type="url"
                            className="form-control"
                            value={field.value ?? ""}
                            onChange={e => field.onChange(e.target.value)}
                            placeholder={intl.formatMessage(
                              marketingManagerMessages.redirectUrlPlaceholder
                            )}
                          />
                        </UrlInputWrapper>
                        {errors.redirectUrl && (
                          <ErrorText>
                            <FormattedMessage {...marketingManagerMessages.validationInvalidUrl} />
                          </ErrorText>
                        )}
                      </FormGroup>
                    )}
                  />
                </SectionDivider>
              </SectionCardBody>
            </SectionCard>

            {/* Desktop section */}
            <SectionCard>
              <SectionCardBody>
                <SectionTitle>
                  <FormattedMessage {...marketingManagerMessages.desktopTitle} />
                </SectionTitle>
                <SectionText>
                  <FormattedMessage {...marketingManagerMessages.desktopResolution} />
                </SectionText>
                <SectionText>
                  <FormattedMessage {...marketingManagerMessages.desktopFormats} />
                </SectionText>

                <div className="mt-4">
                  <Controller
                    control={control}
                    name="desktopImages"
                    render={({ field }) => (
                      <>
                        <MarketingImageField
                          fieldId="desktopImages"
                          value={field.value as Image[]}
                          onChange={field.onChange}
                        />
                        {errors.desktopImages && (
                          <ErrorText>
                            <FormattedMessage
                              {...marketingManagerMessages.validationDesktopRequired}
                            />
                          </ErrorText>
                        )}
                      </>
                    )}
                  />
                </div>
              </SectionCardBody>
            </SectionCard>

            {/* Mobile section */}
            <SectionCard>
              <SectionCardBody>
                <SectionTitle>
                  <FormattedMessage {...marketingManagerMessages.mobileTitle} />
                </SectionTitle>
                <SectionText>
                  <FormattedMessage {...marketingManagerMessages.mobileResolution} />
                </SectionText>
                <SectionText>
                  <FormattedMessage {...marketingManagerMessages.mobileFormats} />
                </SectionText>

                <div className="mt-4">
                  <Controller
                    control={control}
                    name="mobileImages"
                    render={({ field }) => (
                      <>
                        <MarketingImageField
                          fieldId="mobileImages"
                          value={field.value as Image[]}
                          onChange={field.onChange}
                        />
                        {errors.mobileImages && (
                          <ErrorText>
                            <FormattedMessage
                              {...marketingManagerMessages.validationMobileRequired}
                            />
                          </ErrorText>
                        )}
                      </>
                    )}
                  />
                </div>
              </SectionCardBody>
            </SectionCard>

            {/* Action buttons */}
            <ButtonContainer>
              <Button variant="outline-secondary" onClick={handleBack} disabled={isSubmitting}>
                <FormattedMessage {...marketingManagerMessages.back} />
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                <FormattedMessage {...marketingManagerMessages.save} />
              </Button>
            </ButtonContainer>
          </form>
        </PageContainer>
      </ContentWrapper>
    </ImageManagerProvider>
  );
}

export default withAuthorizationVerifier(MarketingManagerForm);
